import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Lock,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useGround } from '@/hooks/useGround';
import { useGroundPublish } from '@/hooks/useGroundPublish';
import { useGroundVoice } from '@/hooks/useGroundVoice';
import { useToast } from '@/hooks/useToast';
import {
  FOUNDATION_DAYS,
  type FoundationDay,
  type FoundationSection,
} from '@/lib/ground/foundations';
import { PILLARS } from '@/lib/ground/pillars';
import { findProtocol } from '@/lib/ground/protocols';
import { cn } from '@/lib/utils';
import { SectionTimer } from './SectionTimer';

interface FoundationsProps {
  onOpenProtocols: (protocolId?: string) => void;
}

export function Foundations({ onOpenProtocols }: FoundationsProps) {
  const { completedDays, foundationsComplete } = useGround();
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  if (selectedDay !== null) {
    const day = FOUNDATION_DAYS[selectedDay - 1];
    return (
      <DayView
        day={day}
        onBack={() => setSelectedDay(null)}
        onOpenProtocols={onOpenProtocols}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-2xl">
        <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          Foundations
        </h2>
        <p className="mt-3 text-muted-foreground">
          A free five-day protocol that introduces all five pillars. Each day is five short
          micro-sections — about fifteen minutes with reading and reflection. No fixing, just
          tracking.
        </p>
      </div>

      {foundationsComplete && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-5">
            <div className="flex items-center gap-3">
              <Sparkles className="size-5 text-primary" />
              <div>
                <div className="font-medium">Foundations complete</div>
                <p className="text-sm text-muted-foreground">
                  You have finished all five days. The Protocols are now unlocked.
                </p>
              </div>
            </div>
            <Button size="sm" className="gap-1.5" onClick={() => onOpenProtocols()}>
              Explore Protocols
              <ChevronRight className="size-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-3 py-6">
          <div className="flex items-end justify-between gap-3">
            <div className="font-display text-2xl font-semibold tabular-nums">
              {completedDays.length} / {FOUNDATION_DAYS.length} days complete
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.round((completedDays.length / FOUNDATION_DAYS.length) * 100)}%
            </div>
          </div>
          <Progress value={(completedDays.length / FOUNDATION_DAYS.length) * 100} className="h-2.5" />
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        {FOUNDATION_DAYS.map((day) => (
          <DayCard key={day.day} day={day} onOpen={() => setSelectedDay(day.day)} />
        ))}
      </div>
    </div>
  );
}

function DayCard({ day, onOpen }: { day: FoundationDay; onOpen: () => void }) {
  const { completedSections, isDayComplete } = useGround();
  const done = day.sections.filter((section) => completedSections.includes(section.id)).length;
  const complete = isDayComplete(day.day);

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'group relative overflow-hidden rounded-xl border bg-card p-5 text-left shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none',
        complete && 'border-primary/40',
      )}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex size-12 shrink-0 items-center justify-center rounded-xl bg-secondary text-2xl',
            complete && 'bg-primary/15',
          )}
          aria-hidden
        >
          {day.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Day {day.day}
            </span>
            {complete && <Check className="size-4 text-primary" />}
          </div>
          <h3 className="font-display text-xl font-semibold">{day.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{day.theme}</p>

          <div className="mt-3 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/10">
              <div
                className={cn('h-full rounded-full transition-all', complete ? 'bg-primary' : 'bg-primary/60')}
                style={{ width: `${(done / day.sections.length) * 100}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-muted-foreground">
              {done}/{day.sections.length}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function DayView({
  day,
  onBack,
  onOpenProtocols,
}: {
  day: FoundationDay;
  onBack: () => void;
  onOpenProtocols: (protocolId?: string) => void;
}) {
  const { completedSections, isDayComplete } = useGround();

  const allSectionsDone = day.sections.every((section) =>
    completedSections.includes(section.id),
  );
  const complete = isDayComplete(day.day);

  if (allSectionsDone || complete) {
    return <DayCompletion day={day} onBack={onBack} onOpenProtocols={onOpenProtocols} />;
  }

  return <DaySections day={day} onBack={onBack} />;
}

function DaySections({ day, onBack }: { day: FoundationDay; onBack: () => void }) {
  const { completedSections, completeSection } = useGround();
  const voice = useGroundVoice();
  const [listeningId, setListeningId] = useState<string | null>(null);

  const done = day.sections.filter((section) => completedSections.includes(section.id)).length;

  const handleListen = (id: string, text: string) => {
    const isActive = listeningId === id && (voice.status === 'playing' || voice.status === 'paused');
    if (isActive) {
      voice.stop();
      setListeningId(null);
      return;
    }
    setListeningId(id);
    voice.play(text);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
          <ArrowLeft className="size-4" />
          All days
        </Button>
        <Badge variant="outline" className="font-normal tabular-nums">
          {done}/{day.sections.length} sections
        </Badge>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-secondary text-3xl" aria-hidden>
          {day.emoji}
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Day {day.day}
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">{day.title}</h2>
          <p className="text-muted-foreground">{day.theme}</p>
        </div>
      </div>

      {voice.error && (
        <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
          {voice.error}
        </p>
      )}

      <div className="space-y-4">
        {day.sections.map((section, index) => (
          <SectionCard
            key={section.id}
            index={index}
            section={section}
            done={completedSections.includes(section.id)}
            listening={listeningId === section.id && (voice.status === 'playing' || voice.status === 'paused')}
            onListen={() => handleListen(section.id, `${section.body} {pause} ${section.action}`)}
            onDone={() => completeSection(section.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SectionCard({
  index,
  section,
  done,
  listening,
  onListen,
  onDone,
}: {
  index: number;
  section: FoundationSection;
  done: boolean;
  listening: boolean;
  onListen: () => void;
  onDone: () => void;
}) {
  const pillar = PILLARS[section.pillar];

  return (
    <Card className={cn('overflow-hidden transition-colors', done && 'border-primary/30')}>
      <div className={cn('bg-gradient-to-br px-5 pt-5 pb-4', pillar.hue)}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg" aria-hidden>
              {pillar.emoji}
            </span>
            <span className={cn('text-xs font-medium uppercase tracking-wide', pillar.accent)}>
              {pillar.label}
            </span>
            <span className="text-xs text-muted-foreground">· Section {index + 1}</span>
          </div>
          {done && (
            <Badge variant="secondary" className="gap-1 font-normal">
              <Check className="size-3" />
              Done
            </Badge>
          )}
        </div>
        <h3 className="mt-2 font-display text-xl font-semibold">{section.title}</h3>
      </div>

      <CardContent className="space-y-4 py-5">
        <p className="text-[15px] leading-relaxed text-foreground/90">{section.body}</p>

        <div className="rounded-lg border border-dashed bg-background/40 px-4 py-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Practice
          </div>
          <p className="mt-1 text-[15px] leading-relaxed">{section.action}</p>
        </div>

        <Separator />

        <div className="flex flex-wrap items-center gap-3">
          {section.timerSec ? (
            <SectionTimer seconds={section.timerSec} done={done} onDone={onDone} />
          ) : (
            !done && (
              <Button size="sm" className="gap-1.5" onClick={onDone}>
                <Check className="size-3.5" />
                Mark done
              </Button>
            )
          )}

          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground"
              onClick={onListen}
            >
              {listening ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
              {listening ? 'Stop' : 'Listen'}
            </Button>
            {section.timerSec && !done && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-muted-foreground"
                onClick={onDone}
              >
                <Check className="size-3.5" />
                Mark done
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DayCompletion({
  day,
  onBack,
  onOpenProtocols,
}: {
  day: FoundationDay;
  onBack: () => void;
  onOpenProtocols: (protocolId?: string) => void;
}) {
  const { journals, saveJournal, completeDay, isDayComplete, addSession, addSatsEarned, settings } =
    useGround();
  const { mutate: publish, isPending } = useGroundPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const alreadyComplete = isDayComplete(day.day);
  const [journal, setJournal] = useState(journals[String(day.day)] ?? '');
  const teaser = findProtocol(day.teaserProtocolId);

  const handleJournalChange = (value: string) => {
    setJournal(value);
    saveJournal(day.day, value);
  };

  const handleComplete = () => {
    const content = `Completed Foundations Day ${day.day} — ${day.title}`;
    publish(
      {
        kind: 1,
        content,
        tags: [
          ['t', 'ground'],
          ['t', 'foundations'],
          ['t', `day-${day.day}`],
          ['alt', content],
        ],
      },
      {
        onSuccess: ({ published }) => {
          completeDay(day.day);
          addSession();
          if (settings.demoMode) addSatsEarned(21);
          queryClient.invalidateQueries({ queryKey: ['ground', 'feed'] });
          toast({
            title: `Day ${day.day} complete`,
            description: published
              ? 'Your Foundations progress was shared to Nostr.'
              : 'Saved locally — relays were unreachable just now.',
          });
        },
        onError: () => {
          toast({
            title: 'Could not publish',
            description: 'Something went wrong signing your completion.',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="size-4" />
        All days
      </Button>

      <Card className="overflow-hidden border-primary/30">
        <div className="bg-gradient-to-br from-primary/20 to-primary/5 px-6 py-8 text-center">
          <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-primary/15 text-3xl" aria-hidden>
            {day.emoji}
          </div>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight">
            Day {day.day} complete
          </h2>
          <p className="mt-2 text-muted-foreground">{day.theme}</p>
        </div>

        <CardContent className="space-y-5 py-6">
          <div className="space-y-3 rounded-xl border border-dashed p-5">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="size-4 text-primary" />
              Reflection
            </div>
            <p className="text-[15px] text-muted-foreground">{day.journalPrompt}</p>
            <Textarea
              value={journal}
              onChange={(event) => handleJournalChange(event.target.value)}
              placeholder="Write whatever surfaced… (kept on this device)"
              rows={4}
              disabled={alreadyComplete}
            />
          </div>

          {!alreadyComplete && (
            <Button size="lg" className="w-full gap-2" onClick={handleComplete} disabled={isPending}>
              <Check className="size-4" />
              Complete Day {day.day}
            </Button>
          )}
        </CardContent>
      </Card>

      {teaser && (
        <Card className={cn('overflow-hidden', !alreadyComplete && 'opacity-90')}>
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2 font-display text-lg">
              <span aria-hidden>{teaser.emoji}</span>
              Go deeper: {teaser.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 py-6">
            <p className="text-[15px] text-muted-foreground">{teaser.description}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="font-normal">
                {teaser.sessionCount} sessions
              </Badge>
              <Badge variant="outline" className="font-normal">
                {teaser.priceSats.toLocaleString()} sats
              </Badge>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto gap-1.5"
                onClick={() => onOpenProtocols(teaser.id)}
              >
                <Lock className="size-3.5" />
                View in Protocols
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
